import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  Avatar,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Center,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiSearch, FiBriefcase, FiClock } from 'react-icons/fi';
import { useAuth } from '../../../contexts/AuthContext';
import ContactTypeBadges from '../../../components/CRM/ContactTypeBadges';
import QuickAddDeal from '../../../components/CRM/QuickAddDeal';
import { useWorkspaceSearch } from './useWorkspaceSearch';

/**
 * "New Deal" from the workbench control bar.
 *
 * QuickAddDeal needs a contact in hand, so this launcher runs a two-step
 * flow: pick the person (same omni-search as the quick-find overlay, recents
 * shown before typing), then the standard quick-deal form for that person.
 * The parent navigates to the workspace when the deal lands.
 */
export default function NewDealLauncher({
  isOpen,
  onClose,
  isCRMAdmin,
  selectedUserId,
  onDealCreated,
}) {
  const { currentUser } = useAuth();
  const [term, setTerm] = useState('');
  const [pickedContact, setPickedContact] = useState(null);

  const hoverBG = useColorModeValue('purple.50', 'purple.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  // Same owner scoping as the control bar's search
  const scope = isCRMAdmin ? (selectedUserId ? 'mine' : 'all') : 'mine';
  const uid = isCRMAdmin ? selectedUserId : currentUser?.uid;
  const { people, loading, isSearchMode } = useWorkspaceSearch(term, scope, uid);

  const handleClose = () => {
    setTerm('');
    setPickedContact(null);
    onClose();
  };

  // Step 2 — the standard quick-deal form for the chosen person
  if (pickedContact) {
    return (
      <QuickAddDeal
        isOpen={isOpen}
        onClose={handleClose}
        contact={pickedContact}
        onDealCreated={(deal) => {
          onDealCreated?.(deal);
          handleClose();
        }}
      />
    );
  }

  // Step 1 — pick the person
  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={FiBriefcase} />
            <Text>New Deal — who is it for?</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={5}>
          <VStack align="stretch" spacing={3}>
            <InputGroup size="md">
              <InputLeftElement pointerEvents="none">
                {loading ? <Spinner size="sm" color="purple.400" /> : <Icon as={FiSearch} color="gray.400" />}
              </InputLeftElement>
              <Input
                autoFocus
                placeholder="Search by name, email, phone, or company…"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
              />
            </InputGroup>

            {!isSearchMode && people.length > 0 && (
              <HStack spacing={2} color={mutedColor}>
                <Icon as={FiClock} boxSize={3.5} />
                <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase">
                  Recently opened
                </Text>
              </HStack>
            )}

            <VStack align="stretch" spacing={1.5} maxH="360px" overflowY="auto">
              {people.map((person) => {
                const { contact, deals } = person;
                return (
                  <Box
                    key={contact.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setPickedContact(contact)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') setPickedContact(contact);
                    }}
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="lg"
                    px={3}
                    py={2}
                    cursor="pointer"
                    _hover={{ bg: hoverBG }}
                  >
                    <HStack spacing={3} minW={0}>
                      <Avatar size="sm" name={`${contact.firstName || ''} ${contact.lastName || ''}`} />
                      <VStack align="stretch" spacing={0} minW={0} flex={1}>
                        <HStack spacing={2} minW={0} flexWrap="wrap">
                          <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>
                            {contact.firstName} {contact.lastName}
                          </Text>
                          <ContactTypeBadges
                            contactTypes={contact.contactTypes}
                            primaryContactType={contact.primaryContactType}
                            maxDisplay={2}
                          />
                        </HStack>
                        <Text fontSize="xs" color={mutedColor} noOfLines={1}>
                          {[contact.email, contact.phone].filter(Boolean).join(' · ') || 'No contact info'}
                        </Text>
                      </VStack>
                      <Badge colorScheme={deals.length > 0 ? 'blue' : 'gray'} fontSize="2xs" flexShrink={0}>
                        {deals.length > 0 ? `${deals.length} deal${deals.length === 1 ? '' : 's'}` : 'No deals'}
                      </Badge>
                    </HStack>
                  </Box>
                );
              })}

              {isSearchMode && !loading && people.length === 0 && (
                <Center py={6}>
                  <Text color={mutedColor} fontSize="sm">
                    No people found for “{term.trim()}” — create the contact first, then start the deal.
                  </Text>
                </Center>
              )}
            </VStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
